import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const signup = async (userData) => {
    const response = await axios.post(`${API}/auth/user/signup`, userData, {
      withCredentials: true
    });
    setUser(response.data.user);
    setIsAuthenticated(true);
    return response.data;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/user/login`, { email, password }, {
      withCredentials: true
    });
    setUser(response.data.user);
    setIsAuthenticated(true);
    return response.data;
  };

  const loginWithGoogle = async (sessionId) => {
    const response = await axios.post(`${API}/auth/user/google`, { session_id: sessionId }, {
      withCredentials: true
    });
    setUser(response.data.user);
    setIsAuthenticated(true);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateProfile = async (data) => {
    const response = await axios.put(`${API}/auth/user/profile`, data, {
      withCredentials: true
    });
    setUser(response.data);
    return response.data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      signup,
      login,
      loginWithGoogle,
      logout,
      updateProfile,
      checkAuth,
      refreshUser: checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
